import { Injectable, inject } from '@angular/core';
import { Firestore, collection, getDocs, doc, setDoc, deleteDoc, updateDoc, addDoc, query, where } from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';

@Injectable({
  providedIn: 'root'
})
export class SalesInvoiceService {
  private firestore = inject(Firestore);
  private storage = inject(Storage);

  async getTemplateItems() {
    const snap = await getDocs(collection(this.firestore, 'sales_invoice_items'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async getSalesInvoices(companyId?: string) {
    const invoicesRef = collection(this.firestore, 'sales_invoices');
    const q = companyId 
      ? query(invoicesRef, where('companyId', '==', companyId)) 
      : invoicesRef;
      
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async saveSalesInvoice(payload: any, invoiceId: string | null) {
    const invoiceRef = invoiceId
      ? doc(this.firestore, 'sales_invoices', invoiceId)
      : doc(collection(this.firestore, 'sales_invoices'));
    await setDoc(invoiceRef, payload, { merge: true });
    return invoiceRef.id;
  }

  async deleteSalesInvoice(invoiceId: string) {
    await deleteDoc(doc(this.firestore, 'sales_invoices', invoiceId));
  }

  async updateInvoiceEmailStatus(invoiceId: string, emailRecipients: string) {
    await updateDoc(doc(this.firestore, 'sales_invoices', invoiceId), {
      emailStatus: 'Sent',
      emailSentAt: new Date().toISOString(),
      emailRecipients: emailRecipients || ''
    });
  }

  async updateInvoiceStatus(invoiceId: string, payload: any) {
    await updateDoc(doc(this.firestore, 'sales_invoices', invoiceId), {
      amountPaid: payload.amountPaid,
      remainingBalance: payload.remainingBalance,
      status: payload.status,
      updatedAt: new Date().toISOString()
    });
  }

  async updateTemplateItem(itemId: string, data: any) {
    await updateDoc(doc(this.firestore, 'sales_invoice_items', itemId), data);
  }

  async addTemplateItem(data: any) {
    const docRef = await addDoc(collection(this.firestore, 'sales_invoice_items'), data);
    return docRef.id;
  }

  async deleteTemplateItem(itemId: string) {
    await deleteDoc(doc(this.firestore, 'sales_invoice_items', itemId));
  }

  async uploadTempPdf(userId: string, invoiceNumber: string, pdfBlob: Blob): Promise<{ downloadURL: string, storagePath: string }> {
    const filename = `users/${userId || 'temp'}/temp_sales_invoices/${invoiceNumber}_${Date.now()}.pdf`;
    const storageRef = ref(this.storage, filename);
    await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
    const downloadURL = await getDownloadURL(storageRef);
    return { downloadURL, storagePath: filename };
  }

  async deleteTempPdf(storagePath: string) {
    await deleteObject(ref(this.storage, storagePath));
  }
}